package org.teddapp.project.ui.finance

import org.teddapp.project.ui.TeddSize

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Expense_list_item
import com.tedd.db.Finance_account
import com.tedd.db.Finance_category
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens

/**
 * Panneau qui s'ouvre quand on coche un item de liste de dépenses.
 * Demande le montant réel + le compte à débiter + une description.
 * Au save, le dépôt coche la ligne et crée une transaction du restant : ce que les
 * sous-dépenses ont déjà payé ([alreadyPaid]) n'est pas débité deux fois.
 */
@Composable
internal fun MarkItemDonePanel(
    item: Expense_list_item,
    listName: String,
    categories: List<Finance_category>,
    accounts: List<Finance_account>,
    defaultAccountId: String?,
    alreadyPaid: Long = 0L,
    onClose: () -> Unit,
    onConfirm: (actual: Long, accountId: String, date: String, description: String, fundId: String?) -> Unit,
) {
    val cat = item.cat_id?.let { id -> categories.firstOrNull { it.id == id } }
    // Une ligne-portefeuille propose ce qu'on y a déjà dépensé ; une ligne ordinaire, le prévu.
    var actual by remember(item.id) { mutableStateOf((item.actual_ ?: item.planned).toString()) }
    var description by remember(item.id) { mutableStateOf(item.name) }
    var accountId by remember(item.id) {
        mutableStateOf(defaultAccountId ?: accounts.firstOrNull { it.currency == "XOF" }?.id ?: accounts.firstOrNull()?.id)
    }
    var date by remember(item.id) { mutableStateOf(TODAY_ISO) }
    var fundId by remember(item.id) { mutableStateOf<String?>(null) }
    val account = accounts.firstOrNull { it.id == accountId }
    val amountValue = actual.replace(",", ".").toDoubleOrNull() ?: 0.0
    val valid = amountValue > 0.0 && accountId != null

    val fullScreen = org.teddapp.project.ui.LocalUseFullScreenForm.current
    Dialog(
        onDismissRequest = onClose,
        properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = true),
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .then(
                    if (fullScreen) Modifier.background(TeddTokens.Bg)
                    else Modifier.background(Color.Black.copy(alpha = 0.35f))
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onClose,
                ),
        ) {
            Column(
                modifier = Modifier
                    .then(
                        if (fullScreen) Modifier.fillMaxSize()
                        else Modifier.align(Alignment.CenterEnd).width(TeddSize.SidePanel).fillMaxHeight()
                    )
                    .background(TeddTokens.Bg)
                    .then(
                        if (fullScreen) Modifier
                        else Modifier.border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    )
                    .then(if (fullScreen) Modifier.imePadding() else Modifier)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {},
                    ),
            ) {
                // Header
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(horizontal = 22.dp, vertical = 16.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "MARQUER COMME DÉPENSÉ",
                            color = TeddTokens.InkMuted,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = 0.6.sp,
                        )
                        Text(
                            text = item.name,
                            color = TeddTokens.Ink,
                            fontSize = 15.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                        Text(
                            text = "Liste : $listName",
                            color = TeddTokens.InkMuted,
                            fontSize = 11.5.sp,
                        )
                    }
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RXs))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs))
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = onClose,
                            )
                            .padding(8.dp),
                    ) {
                        TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted)
                    }
                }
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(22.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    // Planned reminder
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                            .padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        if (cat != null) {
                            Box(
                                modifier = Modifier
                                    .size(28.dp)
                                    .clip(RoundedCornerShape(TeddTokens.RSm))
                                    .background(parseFinHex(cat.color).copy(alpha = 0.12f), RoundedCornerShape(TeddTokens.RSm)),
                                contentAlignment = Alignment.Center,
                            ) {
                                TeddIcon(name = cat.icon, size = 14.dp, color = parseFinHex(cat.color), strokeWidth = 2.dp)
                            }
                            Column(modifier = Modifier.weight(1f)) {
                                Text(text = cat.name, color = TeddTokens.Ink, fontSize = 12.5.sp, fontWeight = FontWeight.Medium)
                                Text(
                                    text = "Prévu " + money(item.planned, account?.currency ?: "XOF"),
                                    color = TeddTokens.InkMuted,
                                    fontSize = 11.5.sp,
                                )
                            }
                        } else {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(text = "Sans catégorie", color = TeddTokens.InkMuted, fontSize = 12.sp)
                                Text(
                                    text = "Prévu " + money(item.planned, account?.currency ?: "XOF"),
                                    color = TeddTokens.InkMuted,
                                    fontSize = 11.5.sp,
                                )
                            }
                        }
                    }

                    // Actual amount
                    Column {
                        Text(
                            text = "Montant réel",
                            color = TeddTokens.InkMuted,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                        Spacer(modifier = Modifier.height(6.dp))
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(TeddTokens.RSm))
                                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                                .padding(horizontal = 14.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            BasicTextField(
                                value = actual,
                                onValueChange = { v -> if (v.all { c -> c.isDigit() || c == '.' || c == ',' }) actual = v },
                                modifier = Modifier.weight(1f),
                                singleLine = true,
                                textStyle = TextStyle(
                                    color = TeddTokens.Ink,
                                    fontSize = 28.sp,
                                    fontWeight = FontWeight.Medium,
                                    letterSpacing = (-0.5).sp,
                                ),
                                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                                visualTransformation = ThousandsVisualTransformation(),
                                decorationBox = { inner ->
                                    if (actual.isEmpty()) Text("0", color = TeddTokens.InkMuted, fontSize = 28.sp)
                                    inner()
                                },
                            )
                            Text(
                                text = if (account?.currency == "USD") "$" else "F",
                                color = TeddTokens.InkMuted,
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                        // Ce que le portefeuille a déjà payé ne se débite pas deux fois.
                        if (alreadyPaid > 0L) {
                            val due = (amountValue.toLong() - alreadyPaid).coerceAtLeast(0L)
                            Spacer(modifier = Modifier.height(6.dp))
                            Text(
                                text = "Déjà payé ${money(alreadyPaid, account?.currency ?: "XOF", compact = true)} par les sous-dépenses · à débiter ${money(due, account?.currency ?: "XOF", compact = true)}",
                                color = TeddTokens.InkMuted,
                                fontSize = 11.5.sp,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                        // Variance hint
                        val plannedF = item.planned.toDouble()
                        if (plannedF > 0.0 && amountValue > 0.0) {
                            val delta = amountValue.toLong() - item.planned
                            val pct = (delta * 100.0 / plannedF).toInt()
                            Spacer(modifier = Modifier.height(6.dp))
                            Text(
                                text = when {
                                    delta == 0L -> "Pile sur le budget."
                                    delta > 0L -> "Au-dessus du prévu : +${money(delta, account?.currency ?: "XOF", compact = true)} ($pct%)."
                                    else -> "Sous le prévu : ${money(-delta, account?.currency ?: "XOF", compact = true)} économisé ($pct%)."
                                },
                                color = when {
                                    delta == 0L -> TeddTokens.InkMuted
                                    delta > 0L -> TeddTokens.Danger
                                    else -> TeddTokens.Success
                                },
                                fontSize = 11.5.sp,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                    }

                    // Account select
                    Column {
                        Text(
                            text = "Débité du compte",
                            color = TeddTokens.InkMuted,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                        Spacer(modifier = Modifier.height(6.dp))
                        FinanceAccountChips(accounts, selectedId = accountId, onSelect = { accountId = it })
                    }

                    // Description + date
                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        Column(modifier = Modifier.weight(1f)) {
                            Text(text = "Description", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                            Spacer(modifier = Modifier.height(6.dp))
                            SimpleTextField(value = description, onValueChange = { description = it })
                        }
                        Column(modifier = Modifier.width(140.dp)) {
                            Text(text = "Date", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                            Spacer(modifier = Modifier.height(6.dp))
                            SimpleTextField(value = date, onValueChange = { date = it })
                        }
                    }
                }
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                        .padding(16.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Spacer(modifier = Modifier.weight(1f))
                    TeddButton(text = "Annuler", kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md, onClick = onClose)
                    TeddButton(
                        text = "Valider la dépense",
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Md,
                        enabled = valid,
                        onClick = {
                            onConfirm(amountValue.toLong(), accountId!!, date, description.ifBlank { item.name }, fundId)
                        },
                        leadingIcon = { TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp) },
                    )
                }
            }
        }
    }
}

@Composable
private fun SimpleTextField(value: String, onValueChange: (String) -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(38.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp),
        contentAlignment = Alignment.CenterStart,
    ) {
        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            singleLine = true,
            textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
            cursorBrush = SolidColor(TeddTokens.LimeStrong),
            modifier = Modifier.fillMaxWidth(),
        )
    }
}
